import React from 'react';
import { PaintBrushIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import { Link } from 'react-router-dom';

const PerformanceFrameworkStage4 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 4: Design your Performance Framework" icon={<PaintBrushIcon className="h-7 w-7 text-primary-light" />} heading>
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <ul className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Answer the four key design questions: What, How, When, and Who are we assessing?</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Build your framework around the three pillars: Results, Execution, and Behaviours</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Create a simple, lightweight rating system (we recommend traffic lights)</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Define timing, frequency, and who gets assessed (spoiler: everyone, including leadership)</span>
            </li>
          </ul>
          <p className="mt-4 text-gray-300">Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Ready to Design the Thing</h3>
        <p className="text-gray-300 mb-6">
          Hopefully you now have your building blocks and you're clear on what purpose this performance framework 
          is going to actually serve. That means you're in a much better place to design the thing.
        </p>

        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6 mb-6">
          <h4 className="text-white font-semibold mb-3">Core Principles Reminder:</h4>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Performance conversations & awareness should be as regular & continuous as possible</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Lightweight & user-friendly beats cumbersome & over-engineered all day long</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Transparency is a powerful lever. Lean into it where you can.</span>
            </li>
          </ul>
        </div>

        <p className="text-gray-300 mb-6">
          It's crucial you design it in the way that works for <strong className="text-white">you & your context.</strong>
        </p>

        <h4 className="text-lg font-semibold text-white mb-4">The Four Key Questions You Need to Answer:</h4>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <div className="flex items-center mb-2">
              <span className="text-primary-light mr-3 font-bold text-lg">1.</span>
              <h5 className="text-primary-light font-semibold">What are we assessing?</h5>
            </div>
            <p className="text-gray-300 text-sm">The core pillars and components of performance</p>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <div className="flex items-center mb-2">
              <span className="text-primary-light mr-3 font-bold text-lg">2.</span>
              <h5 className="text-primary-light font-semibold">How are we assessing it?</h5>
            </div>
            <p className="text-gray-300 text-sm">The rating system and evaluation approach</p>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <div className="flex items-center mb-2">
              <span className="text-primary-light mr-3 font-bold text-lg">3.</span>
              <h5 className="text-primary-light font-semibold">When are we assessing it?</h5>
            </div>
            <p className="text-gray-300 text-sm">Frequency and timing of performance cycles</p>
          </div>
          <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
            <div className="flex items-center mb-2">
              <span className="text-primary-light mr-3 font-bold text-lg">4.</span>
              <h5 className="text-primary-light font-semibold">Who are we assessing?</h5>
            </div>
            <p className="text-gray-300 text-sm">Scope and inclusion across the organization</p>
          </div>
        </div>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <span className="text-primary-light mr-3 font-bold text-2xl">1.</span>
          <h3 className="text-2xl font-semibold text-white">What Are We Assessing?</h3>
        </div>
        <p className="text-gray-300 mb-6">
          We're big believers at Open Org on focusing on 3 core pillars:
        </p>

        <div className="mt-6 p-8 bg-gray-800/50 rounded-lg border border-gray-700 mb-6">
          <div className="flex flex-col items-center space-y-6">
            <div className="text-center">
              <h5 className="text-white font-semibold text-lg mb-4">Performance Framework Formula</h5>
            </div>
            
            <div className="flex items-start justify-center space-x-4">
              <div className="flex flex-col items-center space-y-3">
                <div className="bg-white text-gray-900 px-4 py-2 rounded-lg font-semibold text-base h-10 flex items-center">
                  Results
                </div>
                <div className="text-center">
                  <div className="text-gray-400 text-sm font-medium">What we deliver</div>
                  <div className="text-gray-300 text-xs mt-1">relative to expectations & goals</div>
                </div>
              </div>
              
              <div className="flex items-center h-10 mt-0">
                <span className="text-primary-light font-bold text-xl">+</span>
              </div>
              
              <div className="flex flex-col items-center space-y-3">
                <div className="bg-white text-gray-900 px-4 py-2 rounded-lg font-semibold text-base h-10 flex items-center">
                  Execution
                </div>
                <div className="text-center">
                  <div className="text-gray-400 text-sm font-medium">How effectively we deliver</div>
                  <div className="text-gray-300 text-xs mt-1">in context of expected competencies</div>
                </div>
              </div>
              
              <div className="flex items-center h-10 mt-0">
                <span className="text-primary-light font-bold text-xl">+</span>
              </div>
              
              <div className="flex flex-col items-center space-y-3">
                <div className="bg-white text-gray-900 px-4 py-2 rounded-lg font-semibold text-base h-10 flex items-center">
                  Behaviours
                </div>
                <div className="text-center">
                  <div className="text-gray-400 text-sm font-medium">How we role-model</div>
                  <div className="text-gray-300 text-xs mt-1">expected behaviours day to day</div>
                </div>
              </div>
              
              <div className="flex items-center h-10 mt-0">
                <span className="text-primary-light font-bold text-xl">=</span>
              </div>
              
              <div className="flex items-center">
                <div className="bg-primary text-white px-6 py-3 rounded-lg font-bold text-base h-12 flex items-center">
                  Performance
                </div>
              </div>
            </div>
            
                          {/* Attribution */}
              <div className="text-center mt-6">
                <img 
                  src="https://auth.openorg.fyi/storage/v1/object/public/web-assets/oo-logo/Default%20Transparent%20PNG.png" 
                  alt="Open Org Logo" 
                  className="h-4 opacity-60 mx-auto"
                />
              </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-6">
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
            <h4 className="text-white font-semibold mb-3">Results</h4>
            <p className="text-gray-300 text-sm">What we deliver, relative to expectations & goals</p>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
            <h4 className="text-white font-semibold mb-3">Execution</h4>
            <p className="text-gray-300 text-sm">How effectively & independently we deliver it, in the context of expected competencies</p>
          </div>
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
            <h4 className="text-white font-semibold mb-3">Behaviours</h4>
            <p className="text-gray-300 text-sm">How well we meet & role-model expected behaviours day to day</p>
          </div>
        </div>

        <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
          <p className="text-gray-300 text-sm">
            <strong className="text-white">Important:</strong> It's very hard to assess any of these things fairly & consistently 
            without those earlier building blocks we talked about being in place.
          </p>
        </div>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <span className="text-primary-light mr-3 font-bold text-2xl">2.</span>
          <h3 className="text-2xl font-semibold text-white">How Are We Assessing It?</h3>
        </div>
        <p className="text-gray-300 mb-6">
          We're believers in keeping this as lightweight as possible. We love a traffic light approach, using 3 simple choices, 
          but we've also worked with companies who have opted to add an additional 4th option.
        </p>

        <div className="overflow-x-auto mb-6">
          <table className="w-full border border-gray-700 rounded-lg">
            <thead>
              <tr className="bg-gray-800/50">
                <th className="border border-gray-700 p-4 text-left text-white font-semibold">Outcome</th>
                <th className="border border-gray-700 p-4 text-left text-white font-semibold">Results</th>
                <th className="border border-gray-700 p-4 text-left text-white font-semibold">Execution</th>
                <th className="border border-gray-700 p-4 text-left text-white font-semibold">Behaviours</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-700 p-4">
                  <span className="text-green-400 font-semibold">Exceeding Expectations</span>
                </td>
                <td className="border border-gray-700 p-4 text-gray-300 text-sm">
                  You're delivering more than is expected of you right now
                </td>
                <td className="border border-gray-700 p-4 text-gray-300 text-sm">
                  You're delivering work with a higher level of competency than we'd expect right now
                </td>
                <td className="border border-gray-700 p-4 text-gray-300 text-sm">
                  You're more than meeting behavioural expectations right now
                </td>
              </tr>
              <tr>
                <td className="border border-gray-700 p-4">
                  <span className="text-yellow-400 font-semibold">Meeting Expectations</span>
                </td>
                <td className="border border-gray-700 p-4 text-gray-300 text-sm">
                  You're delivering what is expected of you right now
                </td>
                <td className="border border-gray-700 p-4 text-gray-300 text-sm">
                  You're delivering work to the level of competency we'd expect of you right now
                </td>
                <td className="border border-gray-700 p-4 text-gray-300 text-sm">
                  You're meeting behavioural expectations right now
                </td>
              </tr>
              <tr>
                <td className="border border-gray-700 p-4">
                  <span className="text-red-400 font-semibold">Not Meeting Expectations</span>
                </td>
                <td className="border border-gray-700 p-4 text-gray-300 text-sm">
                  You're not delivering what is expected of you right now
                </td>
                <td className="border border-gray-700 p-4 text-gray-300 text-sm">
                  You're not delivering work to the level of competency we'd expect of your role right now
                </td>
                <td className="border border-gray-700 p-4 text-gray-300 text-sm">
                  You're not meeting behavioural expectations right now. It's likely we have had to speak to you formally or informally about conduct & behaviour
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <ExpandableCallout type="tip" title="Important Considerations" className="mb-6">
          <div className="space-y-4">
            <div>
              <h5 className="text-white font-medium mb-2">Weighting</h5>
              <p className="text-gray-300 text-sm mb-2">
                This will be contextual to your org but it's worth considering what weighting of importance you want to apply to each pillar. 
                E.g. <em>Are results more important than behaviours?</em> Get clear on this upfront.
              </p>
              <div className="bg-gray-800 p-4 rounded border border-gray-700">
                <p className="text-gray-300 text-sm">
                  <strong className="text-white">Our recommendation:</strong> As a general rule we expect to see someone as a minimum showing 
                  two consistent cycles of meeting expectations across 'results' & 'behaviours' and exceeding expectations for 
                  'execution' before promotion to the next level.
                </p>
                                  <p className="text-gray-300 text-sm mt-2">
                    For clarity, anyone not meeting expectations for behaviours will not be considered for progression regardless 
                    of the results they deliver, or how they deliver them.
                  </p>
              </div>
            </div>

            <div>
              <h5 className="text-white font-medium mb-2">4th Rating Option</h5>
              <p className="text-gray-300 text-sm">
                Some orgs we've built with have included a 4th option (e.g. 'Growing' or 'Still Developing') to indicate someone who is 
                maybe ramping up in role as a new employee, or after a recent promotion. Others may opt to have a policy where newer 
                employees or recent promotions are not included in performance review cycles if they fall within [x] months of the cycle.
              </p>
            </div>

            <div>
              <h5 className="text-white font-medium mb-2">Mapping to Tools</h5>
              <p className="text-gray-300 text-sm">
                We don't design anything around specific tools, but you may have an existing HRIS or performance platform you want to map this to. 
                If a 'traffic light' style system isn't possible on your platform, you can easily replace with a simple 1-3 (or 4) scoring system instead.
              </p>
            </div>
          </div>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <span className="text-primary-light mr-3 font-bold text-2xl">3.</span>
          <h3 className="text-2xl font-semibold text-white">When Are We Assessing It?</h3>
        </div>
        <p className="text-gray-300 mb-6">
          Annual or Bi-annual? Quarterly or Continuous? Honestly this is up to you. Recent trends lean towards more continuous 
          rather than annual snapshots, but all options <em>could</em> work in the right set up and culture.
        </p>

        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6 mb-6">
          <h4 className="text-white font-semibold mb-3">Our View</h4>
          <p className="text-gray-300 text-sm mb-3">
            <strong>Bi-Annual feels good</strong>, with some real consistency on effective 1:1s in between cycles to complement that 
            and make performance an ongoing discussion at least every 4 weeks.
          </p>
          <p className="text-gray-300 text-sm">
            <strong>The aim:</strong> Make sure no one in your business goes more than 4 weeks without knowing how they are performing.
          </p>
        </div>

        <h4 className="text-lg font-semibold text-white mb-3">Questions to Help You Decide:</h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300"><strong className="text-white">Feedback:</strong> Are we trying to move towards a more continuous feedback approach, or something more formal and structured?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300"><strong className="text-white">Business Rhythms:</strong> How quickly do goals etc change? Do we need to shorten feedback loops or can we keep them a little longer?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300"><strong className="text-white">Performance:</strong> Would more frequent reviews help people course-correct sooner and grow faster?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300"><strong className="text-white">Managers:</strong> What impact would bi-annual have vs annual on time and commitments? Would it help build that muscle if it were more regular?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300"><strong className="text-white">Employee Voice:</strong> What do people want? Is there any clear signal on the things people are asking for?</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300"><strong className="text-white">Compensation:</strong> How do performance reviews tie into salary/compensation decisions & reviews? When do they happen?</span>
          </li>
        </ul>

                <div className="mb-6 p-6 bg-gray-800/30 border border-gray-700 rounded-lg">
          <div className="text-center mb-8">
            <h4 className="text-white font-semibold text-xl mb-2">6-Week Performance Review Timeline</h4>
            <p className="text-gray-400 text-sm">Jan 9 - Feb 17, 2023 Example</p>
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-4 mb-8 pb-4 border-b border-gray-700">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span className="text-gray-300 text-xs">Everyone</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span className="text-gray-300 text-xs">People Managers</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
              <span className="text-gray-300 text-xs">Peer Reviewers</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
              <span className="text-gray-300 text-xs">Admin</span>
            </div>
          </div>
          
          {/* Timeline with graphic line */}
          <div className="relative">
            {/* Central timeline line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-light via-primary to-primary-light"></div>
            
            <div className="space-y-8">
            
              {/* Week 1 */}
              <div className="relative flex items-start">
                <div className="relative z-10 flex items-center justify-center w-16 h-8 bg-primary text-white text-xs font-bold rounded-full mr-6 flex-shrink-0">
                  Week 1
                </div>
                <div className="flex-1 bg-gray-900/50 border border-gray-600/50 rounded-lg p-4">
                  <div className="text-gray-400 text-xs mb-3">Jan 9-13, 2023</div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Nominate peers</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs">Everyone</span>
                        <span className="text-gray-400 text-xs">Jan 9 → Jan 11</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Write self review</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs">Everyone</span>
                        <span className="text-gray-400 text-xs">Jan 9 → Jan 13</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Write upward review</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs">Everyone</span>
                        <span className="text-gray-400 text-xs">Jan 9 → Jan 13</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Approve peer nominations</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-green-500/20 text-green-300 px-2 py-1 rounded-full text-xs">People Managers</span>
                        <span className="text-gray-400 text-xs">Jan 12 → Jan 13</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Week 2 */}
              <div className="relative flex items-start">
                <div className="relative z-10 flex items-center justify-center w-16 h-8 bg-primary text-white text-xs font-bold rounded-full mr-6 flex-shrink-0">
                  Week 2
                </div>
                <div className="flex-1 bg-gray-900/50 border border-gray-600/50 rounded-lg p-4">
                  <div className="text-gray-400 text-xs mb-3">Jan 16-20, 2023</div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Write peer review(s)</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-orange-500/20 text-orange-300 px-2 py-1 rounded-full text-xs">Peer Reviewers</span>
                        <span className="text-gray-400 text-xs">Jan 16 → Jan 18</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Write downward review(s)</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-green-500/20 text-green-300 px-2 py-1 rounded-full text-xs">People Managers</span>
                        <span className="text-gray-400 text-xs">Jan 16 → Jan 20</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Schedule real-time conversations</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-green-500/20 text-green-300 px-2 py-1 rounded-full text-xs">People Managers</span>
                        <span className="text-gray-400 text-xs">Jan 16 → Jan 20</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Week 3 */}
              <div className="relative flex items-start">
                <div className="relative z-10 flex items-center justify-center w-16 h-8 bg-primary text-white text-xs font-bold rounded-full mr-6 flex-shrink-0">
                  Week 3
                </div>
                <div className="flex-1 bg-gray-900/50 border border-gray-600/50 rounded-lg p-4">
                  <div className="text-gray-400 text-xs mb-3">Jan 23-27, 2023</div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Scottsdale, AZ Retreat Week</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs">Everyone</span>
                        <span className="text-gray-400 text-xs">Jan 23 → Jan 27</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Week 4 */}
              <div className="relative flex items-start">
                <div className="relative z-10 flex items-center justify-center w-16 h-8 bg-primary text-white text-xs font-bold rounded-full mr-6 flex-shrink-0">
                  Week 4
                </div>
                <div className="flex-1 bg-gray-900/50 border border-gray-600/50 rounded-lg p-4">
                  <div className="text-gray-400 text-xs mb-3">Jan 30 - Feb 3, 2023</div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Complete team calibrations</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-green-500/20 text-green-300 px-2 py-1 rounded-full text-xs">People Managers</span>
                        <span className="text-gray-400 text-xs">Jan 30 → Feb 3</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Week 5 */}
              <div className="relative flex items-start">
                <div className="relative z-10 flex items-center justify-center w-16 h-8 bg-primary text-white text-xs font-bold rounded-full mr-6 flex-shrink-0">
                  Week 5
                </div>
                <div className="flex-1 bg-gray-900/50 border border-gray-600/50 rounded-lg p-4">
                  <div className="text-gray-400 text-xs mb-3">Feb 6-10, 2023</div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Real-time performance conversation(s)</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs">Everyone</span>
                        <span className="text-gray-400 text-xs">Feb 6 → Feb 10</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Total rewards recommendations and approval</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-green-500/20 text-green-300 px-2 py-1 rounded-full text-xs">People Managers</span>
                        <span className="text-gray-400 text-xs">Feb 6 → Feb 10</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Week 6 */}
              <div className="relative flex items-start">
                <div className="relative z-10 flex items-center justify-center w-16 h-8 bg-primary text-white text-xs font-bold rounded-full mr-6 flex-shrink-0">
                  Week 6
                </div>
                <div className="flex-1 bg-gray-900/50 border border-gray-600/50 rounded-lg p-4">
                  <div className="text-gray-400 text-xs mb-3">Feb 13-17, 2023</div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Automatic release of review packets</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full text-xs">Admin</span>
                        <span className="text-gray-400 text-xs">Feb 13</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">Promotion and total rewards outcomes shared</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs">Everyone</span>
                        <span className="text-gray-400 text-xs">Feb 13 → Feb 17</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white text-sm">(Optional) Individual development plans</span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-blue-500/20 text-blue-300 px-2 py-1 rounded-full text-xs">Everyone</span>
                        <span className="text-gray-400 text-xs">Feb 13 → Feb 17</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
            </div>
          </div>
          
          <div className="mt-6 pt-4 border-t border-gray-700">
            <p className="text-gray-400 text-xs text-center">
              Example from Column's Open-Sourced Performance Framework - Bi-annual cycle (6 weeks total)
            </p>
          </div>
        </div>

        <p className="text-gray-300 text-sm">
          You might like to document & share a visual to help folks build a picture of what a typical cycle looks like too.
        </p>
      </PlaybookCard>

      <PlaybookCard>
        <div className="flex items-center mb-4">
          <span className="text-primary-light mr-3 font-bold text-2xl">4.</span>
          <h3 className="text-2xl font-semibold text-white">Who Are We Assessing?</h3>
        </div>
        <p className="text-gray-300 mb-6">
          This sounds obvious, but we want to call out two things here we see go wrong in a lot of companies.
        </p>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6 mb-6">
          <h4 className="text-white font-semibold mb-3">Leadership & Managers</h4>
          <p className="text-gray-300 text-sm mb-3">
            Lots of companies do a much better job at assessing 'downwards'. Whilst many give folks the opportunity to review 
            their managers, the way that assessment happens is often fraught with inconsistency and vagueness. That problem 
            gets worse the further up the chain you go.
          </p>
          <p className="text-gray-300 text-sm">
            <strong className="text-white">So it's worth just being clear on exactly who is being reviewed.</strong>
          </p>
        </div>

        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6 mb-6">
          <h4 className="text-white font-semibold mb-3">Hint: It should be EVERYONE. And we mean, everyone. CEO's & Founders too.</h4>
        </div>

        <h4 className="text-lg font-semibold text-white mb-3">Make sure you have:</h4>
        <ul className="space-y-3 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">The right questions & set up in your review cycle to enable some form of upwards review from your wider team to your CEO/Founders</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">The right questions to ensure relevant, impactful feedback on your managers performance. They should have their own responsibilities & accountabilities just like ICs</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">You should still be able to formally assess <strong className="text-white">everyone</strong> against the Results, Execution, and Behaviours framework</span>
          </li>
        </ul>

        <ExpandableCallout type="tip" title="Example Performance Frameworks for Inspiration" className="mb-6">
          <div className="space-y-3">
            <div>
              <a href="https://juro.notion.site/Performance-framework-1afc0a7408c3498bbd888352653f4cff" 
                 className="text-primary hover:text-primary-light" target="_blank" rel="noopener noreferrer">
                Juro's Performance Framework
              </a>
              <p className="text-gray-300 text-sm">Excellent lightweight, transparent approach</p>
            </div>
            <div>
              <a href="https://duneanalytics.notion.site/Careers-at-Dune-f25e0b5e1f624d2aaf3ef2c81558ea96" 
                 className="text-primary hover:text-primary-light" target="_blank" rel="noopener noreferrer">
                Dune Analytics Careers Framework
              </a>
              <p className="text-gray-300 text-sm">Comprehensive careers and performance approach</p>
            </div>
            <div>
              <a href="https://sourcegraph.notion.site/High-Performance-Culture-Teammate-Development-2d30e81c0c1a42b9b39beaab8eaed4bb" 
                 className="text-primary hover:text-primary-light" target="_blank" rel="noopener noreferrer">
                Sourcegraph's High Performance Culture
              </a>
              <p className="text-gray-300 text-sm">Deep dive into teammate development practices</p>
            </div>
            <div>
              <a href="https://toggl.notion.site/How-We-Measure-Performance-7d0fd16c0ab749f5b37cddfc6e30511d" 
                 className="text-primary hover:text-primary-light" target="_blank" rel="noopener noreferrer">
                Toggl's Performance Measurement
              </a>
              <p className="text-gray-300 text-sm">Transparent performance measurement approach</p>
            </div>
          </div>
        </ExpandableCallout>

        <ExpandableCallout type="info" title="What This Looks Like in Practice: Company Examples" defaultOpen={false}>
          <div className="space-y-6">
            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
              <h5 className="text-white font-medium mb-3">Juro (50-100 people, Legal Tech)</h5>
              <div className="space-y-2">
                <p className="text-gray-300 text-sm"><strong>Approach:</strong> Simple 3-point scale, bi-annual cycles</p>
                <p className="text-gray-300 text-sm"><strong>Focus:</strong> Results (40%), Execution (40%), Behaviours (20%)</p>
                <p className="text-gray-300 text-sm"><strong>What works:</strong> Transparent ratings, clear promotion criteria, manager-led conversations</p>
                <p className="text-gray-300 text-sm"><strong>Frequency:</strong> Bi-annual formal + monthly 1:1 check-ins</p>
              </div>
            </div>
            
            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
              <h5 className="text-white font-medium mb-3">Buffer (100+ people, Social Media)</h5>
              <div className="space-y-2">
                <p className="text-gray-300 text-sm"><strong>Approach:</strong> Quarterly reviews with continuous feedback</p>
                <p className="text-gray-300 text-sm"><strong>Focus:</strong> Role mastery + Buffer values alignment</p>
                <p className="text-gray-300 text-sm"><strong>What works:</strong> Public salary formula, peer feedback, growth-focused conversations</p>
                <p className="text-gray-300 text-sm"><strong>Frequency:</strong> Quarterly + weekly 1:1s with performance thread</p>
              </div>
            </div>
            
            <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
              <h5 className="text-white font-medium mb-3">GitLab (1000+ people, DevOps)</h5>
              <div className="space-y-2">
                <p className="text-gray-300 text-sm"><strong>Approach:</strong> Values-based performance with OKRs integration</p>
                <p className="text-gray-300 text-sm"><strong>Focus:</strong> Results delivery + Values demonstration + Career development</p>
                <p className="text-gray-300 text-sm"><strong>What works:</strong> Everything documented publicly, 360 feedback, promotion calibration</p>
                <p className="text-gray-300 text-sm"><strong>Frequency:</strong> Annual formal + monthly informal + continuous feedback</p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-primary/10 border border-primary/30 rounded-lg">
              <p className="text-gray-300 text-sm">
                <strong className="text-white">Key Pattern:</strong> Successful frameworks start simple and evolve. Notice how each company adapted the basic pillars to their culture, size, and values.
              </p>
            </div>
          </div>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Your Framework Template</h3>
        
        <CopyToNotion title="Performance Framework Design Template">
          <div className="space-y-6">
            <div>
              <h4 className="font-semibold text-white mb-3">Our Performance Framework</h4>
              
              <div className="space-y-4">
                <div>
                  <h5 className="font-medium text-white mb-2">What We Assess</h5>
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <p className="text-gray-300 text-sm mb-2">We assess performance across three pillars:</p>
                    <ul className="space-y-1 text-gray-300 text-sm">
                      <li>• <strong>Results:</strong> What we deliver, relative to expectations & goals</li>
                      <li>• <strong>Execution:</strong> How effectively & independently we deliver it</li>
                      <li>• <strong>Behaviours:</strong> How well we meet & role-model expected behaviours</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-white mb-2">How We Assess</h5>
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <p className="text-gray-300 text-sm mb-2">We use a simple traffic light system:</p>
                    <ul className="space-y-1 text-gray-300 text-sm">
                      <li>• <span className="text-green-400 font-semibold">Exceeding Expectations:</span> Consistently delivering above what's expected</li>
                      <li>• <span className="text-yellow-400 font-semibold">Meeting Expectations:</span> Reliably delivering what's expected</li>
                      <li>• <span className="text-red-400 font-semibold">Not Meeting Expectations:</span> Not delivering what's expected</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-white mb-2">When We Assess</h5>
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <p className="text-gray-300 text-sm">
                      [Choose: Annual / Bi-annual / Quarterly] formal reviews, with ongoing feedback in monthly 1:1s. 
                      Our goal: no one goes more than 4 weeks without knowing how they're performing.
                    </p>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-white mb-2">Who We Assess</h5>
                  <div className="bg-gray-800 p-3 rounded border border-gray-700">
                    <p className="text-gray-300 text-sm">
                      Everyone in the organization, from individual contributors to leadership team, including CEO. 
                      All roles are assessed against the same framework, with role-specific expectations defined in job descriptions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CopyToNotion>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Outcome</h3>
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-6">
          <h4 className="text-white font-semibold mb-3">By the end of this stage you should have:</h4>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Drafted V1 of your new framework answering all four key questions</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Clear assessment criteria for Results, Execution, and Behaviours</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">Defined timing, frequency, and scope of who gets assessed</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">✓</span>
              <span className="text-gray-300">A simple, lightweight rating system that works for your context</span>
            </li>
          </ul>
          <div className="mt-4 pt-4 border-t border-primary/30">
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Next:</strong> Move to <Link to="/playbooks/how-to-build-launch-embed-performance/stage-5" className="text-primary hover:text-primary-light">Stage 5: Launch it Well </Link> 
              to execute a successful rollout with manager buy-in and clear communication.
            </p>
          </div>
        </div>
      </PlaybookCard>

      <ExpandableCallout type="warning" title="Where You Might Face Resistance" defaultOpen={false}>
        <div className="space-y-4">
          <p className="text-gray-300">
            This stage is littered with potential moments for resistance. What specifically do we rate? What weighting do we give to 
            results vs behaviours? What scale & rating system do we use? What tooling do we use? How often do we do it?
          </p>
          <p className="text-gray-300">
            The most important thing to focus on if this happens is to bring the conversation back to your core principles not just 
            around this framework but as a business.
          </p>
          <div className="bg-gray-800/50 p-4 rounded-lg border-l-4 border-primary-light">
            <p className="text-gray-300">
              <strong className="text-white">Pro tip:</strong> Why are you building this, what for? who for? and what's going to fit your 
              natural culture & ways of work when you think about speed, decision-making, communication. You need some design principles 
              to lean on here. These should help with any resistance you face.
            </p>
          </div>
        </div>
      </ExpandableCallout>
    </PlaybookSection>
  </div>
);

export default PerformanceFrameworkStage4; 